import { useState } from "react";
import api from '@/lib/api';
import { toast } from "@/lib/toast-with-sound";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Youtube, Trash2, ExternalLink, Plus, Loader2, Video } from "lucide-react";
import { BACKEND_URL } from "../config";
const extractYoutubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([^&\n?#]+)/,
    /youtube\.com\/shorts\/([^&\n?#]+)/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

const extractVimeoId = (url) => {
  if (!url) return null;
  const patterns = [
    /player\.vimeo\.com\/video\/(\d+)/,
    /vimeo\.com\/channels\/[^/]+\/(\d+)/,
    /vimeo\.com\/groups\/[^/]+\/videos\/(\d+)/,
    /vimeo\.com\/(\d+)/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

const detectPlatform = (url) => {
  if (!url) return null;
  if (extractYoutubeId(url)) return "youtube";
  if (extractVimeoId(url)) return "vimeo";
  return null;
};

const getVideoEmbedUrl = (video) => {
  if (video.platform === "vimeo" && video.vimeo_id) {
    return `https://player.vimeo.com/video/${video.vimeo_id}`;
  }
  if (video.youtube_id) {
    return `https://www.youtube.com/embed/${video.youtube_id}`;
  }
  return null;
};

const getVideoExternalUrl = (video) => {
  if (video.platform === "vimeo" && video.vimeo_id) {
    return `https://vimeo.com/${video.vimeo_id}`;
  }
  if (video.youtube_id) {
    return `https://www.youtube.com/watch?v=${video.youtube_id}`;
  }
  return video.url;
};

const PlatformIcon = ({ platform, className = "h-4 w-4" }) => {
  if (platform === "vimeo") {
    return <Video className={`${className} text-sky-500`} />;
  }
  return <Youtube className={`${className} text-red-500`} />;
};

/**
 * YoutubeVideoManager - Reusable component for managing YouTube and Vimeo videos
 */
export function YoutubeVideoManager({ 
  videos = [], 
  onVideosChange,
  canEdit = false,
  title = "Videos",
  emptyTitle,
  emptyMessage,
  showHeader = true,
  showAddButton = true,
  showEmptyButton = true,
  gridCols = "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
  compact = false
}) {
  const { t } = useTranslation("common");
  const [modalOpen, setModalOpen] = useState(false);
  const [newVideoUrl, setNewVideoUrl] = useState("");
  const [newVideoTitle, setNewVideoTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [fetchingTitle, setFetchingTitle] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);
  const [detectedPlatform, setDetectedPlatform] = useState(null);

  const fetchVideoTitle = async (url) => {
    const platform = detectPlatform(url);
    if (!platform) return;
    
    try {
      setFetchingTitle(true);
      const response = await api.get("/api/video/title", {
        params: { url }
      });
      if (response.data?.title) {
        setNewVideoTitle(response.data.title);
      }
    } catch (error) {
      console.error("Error fetching video title:", error);
    } finally {
      setFetchingTitle(false);
    }
  };

  const handleVideoUrlChange = (url) => {
    setNewVideoUrl(url);
    const platform = detectPlatform(url);
    setDetectedPlatform(platform);
    if (url && platform && !newVideoTitle) {
      fetchVideoTitle(url);
    }
  };

  const handleAddVideo = async () => {
    if (!newVideoUrl.trim()) {
      toast.error(t("video_manager.url_required", "Por favor, insira um URL de vídeo"));
      return;
    }
    
    const platform = detectPlatform(newVideoUrl);
    if (!platform) {
      toast.error(t("video_manager.invalid_url", "URL inválido. Suportamos YouTube e Vimeo."));
      return;
    }
    
    const youtubeId = platform === "youtube" ? extractYoutubeId(newVideoUrl) : null;
    const vimeoId = platform === "vimeo" ? extractVimeoId(newVideoUrl) : null;
    const platformId = youtubeId || vimeoId;
    
    if (videos.some(v => (v.youtube_id && v.youtube_id === youtubeId) || (v.vimeo_id && v.vimeo_id === vimeoId))) {
      toast.error(t("video_manager.duplicate", "Este vídeo já foi adicionado"));
      return;
    }
    
    const newVideo = {
      video_id: `video_${Date.now()}`,
      url: newVideoUrl,
      platform,
      ...(youtubeId && { youtube_id: youtubeId }),
      ...(vimeoId && { vimeo_id: vimeoId }),
      title: newVideoTitle.trim() || null,
      order: videos.length
    };
    
    const updatedVideos = [...videos, newVideo];
    
    try {
      setSaving(true);
      await onVideosChange(updatedVideos);
      setNewVideoUrl("");
      setNewVideoTitle("");
      setDetectedPlatform(null);
      setModalOpen(false);
      toast.success(t("youtube_manager.video_added"));
    } catch (error) {
      console.error("Error saving video:", error);
      const errorMessage = error.response?.data?.detail || t("youtube_manager.save_error");
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveVideo = async (videoId) => {
    const updatedVideos = videos.filter(v => v.video_id !== videoId);
    try {
      await onVideosChange(updatedVideos);
      toast.success(t("youtube_manager.video_deleted"));
    } catch (error) {
      console.error("Error removing video:", error);
      toast.error(t("youtube_manager.delete_error"));
    }
    setVideoToDelete(null);
  };

  if (videos.length === 0 && !canEdit) {
    return null;
  }

  const previewEmbedUrl = (() => {
    if (!newVideoUrl) return null;
    const platform = detectPlatform(newVideoUrl);
    if (platform === "youtube") {
      return `https://www.youtube.com/embed/${extractYoutubeId(newVideoUrl)}`;
    }
    if (platform === "vimeo") {
      return `https://player.vimeo.com/video/${extractVimeoId(newVideoUrl)}`;
    }
    return null;
  })();

  return (
    <div data-testid="youtube-video-manager">
      {canEdit && showAddButton && (
        <div className="flex items-center justify-end py-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={() => setModalOpen(true)}
                  className="gap-1.5 bg-primary hover:bg-primary/90"
                  data-testid="add-video-btn"
                >
                  <Plus className="h-4 w-4" />
                  {t("youtube_manager.add_video")}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("video_manager.add_tooltip", "Adicionar um novo vídeo do YouTube ou Vimeo")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}
      
      {videos.length === 0 && canEdit && showAddButton && (
        <div className="py-8 text-center rounded-lg bg-card/80 backdrop-blur-sm border border-border/50">
          <Video className="h-10 w-10 mx-auto mb-2 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">{emptyMessage || t("youtube_manager.empty_message")}</p>
        </div>
      )}
      
      {videos.length > 0 && (
        <div className={`grid ${compact ? 'grid-cols-1' : gridCols} gap-4 ${showHeader ? 'pt-2' : ''}`}>
          {videos.map((video) => (
            <Card 
              key={video.video_id} 
              className="bg-card/90 backdrop-blur-sm border-border overflow-hidden group"
              data-testid={`video-card-${video.video_id}`}
            >
              <div className="relative aspect-video">
                <iframe
                  src={getVideoEmbedUrl(video)}
                  title={video.title || "Video"}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
                {canEdit && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2 h-8 w-8 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                    onClick={() => setVideoToDelete(video)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <PlatformIcon platform={video.platform} className="h-3.5 w-3.5 flex-shrink-0" />
                    <p className="text-sm font-medium truncate">
                      {video.title || t("youtube_manager.no_title")}
                    </p>
                  </div>
                  <a
                    href={getVideoExternalUrl(video)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-primary transition-colors flex-shrink-0"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Video className="h-5 w-5 text-primary" />
              {t("video_manager.modal_title", "Adicionar Vídeo")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 overflow-y-auto flex-1">
            <div className="space-y-2">
              <Label htmlFor="video-url">{t("video_manager.url_label", "URL do Vídeo (YouTube ou Vimeo)")}</Label>
              <Input
                id="video-url"
                type="url"
                placeholder="https://www.youtube.com/watch?v=... ou https://vimeo.com/..."
                value={newVideoUrl}
                onChange={(e) => handleVideoUrlChange(e.target.value)}
                data-testid="video-url-input"
              />
              {detectedPlatform && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <PlatformIcon platform={detectedPlatform} className="h-3.5 w-3.5" />
                  <span>{detectedPlatform === "youtube" ? "YouTube" : "Vimeo"}</span>
                </div>
              )}
            </div>
            
            {(fetchingTitle || newVideoTitle) && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 rounded-md px-3 py-2">
                {fetchingTitle ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{t("youtube_manager.fetching_title")}</span>
                  </>
                ) : (
                  <>
                    <PlatformIcon platform={detectedPlatform} />
                    <span className="truncate font-medium text-foreground">{newVideoTitle}</span>
                  </>
                )}
              </div>
            )}
            
            {previewEmbedUrl && (
              <div className="aspect-video w-full rounded-lg overflow-hidden bg-muted">
                <iframe
                  src={previewEmbedUrl}
                  title="Preview"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                />
              </div>
            )}
          </div>
          
          <div className="flex-shrink-0 pt-2">
            <Button 
              variant="save"
              onClick={handleAddVideo} 
              disabled={saving || !newVideoUrl.trim() || fetchingTitle}
              className="w-full"
              data-testid="add-video-submit-btn"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              {t("youtube_manager.add_video")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!videoToDelete} onOpenChange={(open) => !open && setVideoToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("youtube_manager.delete_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("youtube_manager.delete_confirm")}
              {videoToDelete?.title && (
                <> &ldquo;{videoToDelete.title}&rdquo;</>
              )}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel type="button">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction type="button" onClick={() => handleRemoveVideo(videoToDelete?.video_id)}>
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export { getVideoEmbedUrl, getVideoExternalUrl, PlatformIcon, detectPlatform, extractYoutubeId, extractVimeoId };

export default YoutubeVideoManager;
